<template>
  <div class="modal-card">
    <CloseIcon :size="24" class="close-modal-icon close-on-click" />
    <div class="modal-card-header">
      <h1 class="title">{{ title }}</h1>
      <p class="subtitle">{{ subtitle }}</p>
    </div>
    <div class="modal-card-body">
      <div v-if="$slots.extra" class="extra-section">
        <slot name="extra" />
      </div>
      <div v-if="$slots.extra" class="section-heading">Columns</div>
      <div class="column-list">
        <div
          v-for="column in availableColumns"
          :key="column.key"
          class="column-item"
        >
          <div class="column-info">
            <div class="label-with-icon">
              <span class="column-label">{{ column.label }}</span>
              <Tooltip v-if="column.description" :text="column.description">
                <InformationIcon :size="16" class="info-icon" />
              </Tooltip>
            </div>
          </div>
          <SwitchInput
            :modelValue="visibleColumns[column.key]"
            @update:modelValue="toggleColumn(column.key, $event)"
          />
        </div>
      </div>
    </div>
    <div class="modal-card-footer">
      <button class="button secondary form-button close-on-click">
        Done
      </button>
    </div>
  </div>
</template>

<script>
import CloseIcon from 'vue-material-design-icons/Close.vue';
import InformationIcon from 'vue-material-design-icons/Information.vue';
import SwitchInput from '@/components/inputs/SwitchInput.vue';
import Tooltip from '@/components/common/Tooltip.vue';

export default {
  name: 'ColumnSettingsModal',
  components: {
    CloseIcon,
    InformationIcon,
    SwitchInput,
    Tooltip
  },
  props: {
    columns: {
      type: Array,
      required: true,
      // Array of { key: string, label: string }
    },
    storageKey: {
      type: String,
      required: true
    },
    initialVisibility: {
      type: Object,
      default: () => ({})
    },
    title: {
      type: String,
      default: 'Column Settings'
    },
    subtitle: {
      type: String,
      default: 'Choose which columns to display in the table'
    }
  },
  emits: ['close', 'update:visibility'],
  data() {
    return {
      visibleColumns: {}
    };
  },
  computed: {
    availableColumns() {
      // Return columns that can be toggled (exclude special columns)
      return this.columns.filter(col => !this.isFixedColumn(col.key));
    }
  },
  created() {
    // Initialize visibility state
    this.visibleColumns = { ...this.initialVisibility };
  },
  methods: {
    isFixedColumn(key) {
      // Some columns should always be visible (like first column, actions)
      const fixedColumns = ['patientName', 'ticketNumber', 'receivable', 'payment', 'productName', 'actions'];
      return fixedColumns.includes(key);
    },
    toggleColumn(key, visible) {
      this.visibleColumns[key] = visible;
      this.$emit('update:visibility', this.visibleColumns);
    }
  }
}
</script>

<style scoped>
.subtitle {
  font-size: 14px;
  color: #6b7280;
  margin-top: 8px;
}

.column-list {
  display: flex;
  flex-direction: column;
  max-height: 400px;
  overflow-y: auto;
}

.column-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #f3f4f6;
}

.column-item:last-child {
  border-bottom: none;
}

.column-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.label-with-icon {
  display: flex;
  align-items: center;
  gap: 6px;
}

.column-label {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

.info-icon {
  color: #9ca3af;
  cursor: help;
  flex-shrink: 0;
}

.modal-card-body {
  padding-top: 0;
}

.extra-section {
  padding: 12px 0;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 8px;
}

.section-heading {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 8px 0 4px 0;
}
</style>
